<template>
  <div class="pdf-viewer-container">
    <!-- Sección de Seleccionar Firmante -->
    <div class="card mb-3">
      <div class="card-body">
        <h6 class="mb-3 d-flex align-items-center gap-2">
          <i class="fas fa-users text-primary"></i>
          Seleccionar Firmante
        </h6>
        <!-- Contenedor para tarjetas de firmantes (renderizado manual) -->
        <div ref="firmantesCardsContainer" class="d-flex flex-wrap gap-2">
          <!-- Las tarjetas se renderizan manualmente mediante renderizarTarjetasFirmantes() -->
        </div>
        
        <button class="btn btn-secondary btn-sm mt-3" @click="limpiarSeleccion">
          <i class="fas fa-trash-alt me-2"></i>
          Limpiar selección
        </button>
      </div>
    </div>
    
    <!-- Controles de navegación y documento -->
    <div v-if="cargandoPdf" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando PDF...</span>
      </div>
      <p class="mt-2 text-muted">Cargando documento...</p>
    </div>
    
    <div v-else-if="pdfError" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle me-2"></i>
      Error al cargar el PDF: {{ pdfError }}
    </div>
    
    <div v-else-if="pdfCargado">
      <div class="navigation-controls mb-3 d-flex justify-content-between align-items-center">
        <button class="btn btn-sm btn-outline-secondary" 
          :disabled="paginaActual === 1" 
          @click="cambiarPagina(paginaActual - 1)">
          <i class="fas fa-chevron-left me-1"></i>
          Anterior
        </button>
        <span class="text-muted">
          Página {{ paginaActual }} de {{ totalPaginas }}
        </span>
        <button class="btn btn-sm btn-outline-secondary" 
          :disabled="paginaActual === totalPaginas" 
          @click="cambiarPagina(paginaActual + 1)">
          Siguiente
          <i class="fas fa-chevron-right ms-1"></i>
        </button>
      </div>
          
      <!-- Contenedor del PDF con overlay -->
      <div class="pdf-container" style="position: relative; display: inline-block;">
        <img ref="imgPdf" 
          class="img-pdf"
          style="border: 3px solid darkgray; border-radius: 5px; max-height: 600px; width: auto; display: block;"
          :src="pages[paginaActual - 1]?.dataUrl"
          alt="Página del PDF" />
        
        <!-- Overlay para recuadros de firmas -->
        <div ref="overlayContainer" 
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10;">
        </div>
        
        <!-- Overlay para eventos de mouse -->
        <div ref="interactionOverlay"
          @mousemove="onMouseMove"
          @click="onClick"
          @mouseleave="onMouseLeave"
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: auto; z-index: 5; cursor: crosshair;">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import axiosInstance from '@/axios'

const props = defineProps({
  firmantes: {
    type: Array,
    required: true
  },
  registroSeleccionado: {
    type: Object,
    required: true
  },
  esquemaId: {
    type: [String, Number],
    required: false
  },
  showNotification: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['posicion-guardada', 'posiciones-limpias'])

// Estados del PDF
const pdfCargado = ref(false)
const cargandoPdf = ref(false)
const pdfError = ref(null)
const pdfDoc = ref(null)
const pages = ref([])
const totalPaginas = ref(0)
const paginaActual = ref(1)

// Estados de selección
const numFirmante = ref(0)
const posicionesMap = ref(new Map()) // firmanteId -> {x, y, pagina, px, py}

// Referencias DOM
const imgPdf = ref(null)
const overlayContainer = ref(null)
const interactionOverlay = ref(null)
const firmantesCardsContainer = ref(null)

// Colores para los marcadores
const colores = [
  "#74b9ff", "#a29bfe", "#55efc4", "#81ecec", "#ff9ff3",
  "#fd79a8", "#ff7675", "#b2bec3", "#fab1a0", "#5352ed"
]

// Funciones del PDF
const getPdfjs = () => {
  if (typeof window === 'undefined' || !window.pdfjsLib) {
    throw new Error('PDF.js no está cargado. Verifica que el script esté en index.html')
  }
  return window.pdfjsLib
}

const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

const cargarPdf = async () => {
  cargandoPdf.value = true
  pdfError.value = null
  pages.value = []
  
  try {
    const pdfjs = getPdfjs()
    
    const seleccionado = props.registroSeleccionado?.value || props.registroSeleccionado
    if (!seleccionado?.id) {
      throw new Error('No hay documento seleccionado')
    }
    
    // Obtener el esquemaId desde props o desde registroSeleccionado como fallback
    const esquemaId = props.esquemaId || props.registroSeleccionado?.esquema_id || props.registroSeleccionado?.esquema?.id
    if (!esquemaId) {
      throw new Error('No se pudo obtener el ID del esquema')
    }
    
    const response = await axiosInstance.get(`api/ficheroRegistro/${esquemaId}/${seleccionado.id}`)
    
    if (!response.data?.base64) {
      throw new Error('No se pudo obtener el archivo PDF')
    }
    
    const arrayBuffer = base64ToArrayBuffer(response.data.base64)
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer })
    pdfDoc.value = await loadingTask.promise
    totalPaginas.value = pdfDoc.value.numPages
    
    // Cargar todas las páginas
    for (let i = 1; i <= totalPaginas.value; i++) {
      const page = await pdfDoc.value.getPage(i)
      const viewport = page.getViewport({ scale: 1.5 })
      
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.height = viewport.height
      canvas.width = viewport.width
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise
      
      pages.value.push({
        pageNum: i,
        dataUrl: canvas.toDataURL('image/png'),
        viewport: viewport
      })
    }
    
    pdfCargado.value = true
    paginaActual.value = 1
    await nextTick()
    renderizarMarcadores()
  } catch (error) {
    console.error('Error al cargar el PDF:', error)
    pdfError.value = error.message || 'Error desconocido al cargar el PDF'
  } finally {
    cargandoPdf.value = false
  }
}

const cambiarPagina = (nuevaPagina) => {
  if (nuevaPagina >= 1 && nuevaPagina <= totalPaginas.value) {
    paginaActual.value = nuevaPagina
    nextTick(() => {
      renderizarMarcadores()
    })
  }
}

// Funciones de renderizado
const renderizarMarcadores = () => {
  if (!overlayContainer.value || !imgPdf.value) return
  
  // Limpiar marcadores existentes de forma segura
  while (overlayContainer.value.firstChild) {
    overlayContainer.value.removeChild(overlayContainer.value.firstChild)
  }
  
  // Renderizar marcadores usando el mapa de posiciones
  props.firmantes.forEach((item, idx) => {
    const firmanteId = item.firmante.id
    const posicion = posicionesMap.value.get(firmanteId)
    
    if (posicion && posicion.pagina === paginaActual.value) {
      const marker = document.createElement('div')
      marker.setAttribute('data-firmante-id', firmanteId)
      marker.className = 'firma-marcador'
      
      // Obtener el nombre del contacto
      const nombreCompleto = `${item.firmante.nombre || ''} ${item.firmante.apellidos || ''}`.trim()
      const textoMostrar = nombreCompleto || `Firmante ${item.ordenFirma || (idx + 1)}`
      
      // Aplicar estilos directamente - borde rectangular con fondo transparente
      Object.assign(marker.style, {
        position: 'absolute',
        backgroundColor: 'transparent',
        border: `2px solid ${colores[idx % colores.length]}`,
        color: colores[idx % colores.length],
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
        textAlign: 'center',
        minWidth: '120px',
        height: 'auto',
        minHeight: '28px',
        lineHeight: '20px',
        top: `${posicion.y}px`,
        left: `${posicion.x}px`,
        zIndex: '100',
        pointerEvents: 'none',
        userSelect: 'none',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '200px'
      })
      
      marker.textContent = textoMostrar
      
      // Añadir al contenedor de forma segura
      try {
        overlayContainer.value.appendChild(marker)
      } catch (error) {
        console.warn('[renderizarMarcadores] Error al añadir marcador:', error.message)
      }
    }
  })
}

// Renderizar tarjetas de firmantes manualmente (sin v-for para evitar conflictos con Vue)
const renderizarTarjetasFirmantes = () => {
  if (!firmantesCardsContainer.value) return
  
  // Limpiar tarjetas existentes
  while (firmantesCardsContainer.value.firstChild) {
    firmantesCardsContainer.value.removeChild(firmantesCardsContainer.value.firstChild)
  }
  
  // Renderizar cada tarjeta manualmente
  props.firmantes.forEach((item, index) => {
    const card = document.createElement('div')
    card.className = 'firmante-card'
    card.setAttribute('data-firmante-index', index)
    
    const isSelected = index === numFirmante.value
    const hasPosition = !!posicionesMap.value.get(item.firmante.id)
    
    // Estilos de la tarjeta
    Object.assign(card.style, {
      minWidth: '200px',
      flex: '1 1 auto',
      padding: '12px 16px',
      borderRadius: '12px',
      border: '2px solid',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      borderColor: isSelected ? '#409eff' : (hasPosition ? '#10b981' : '#e5e7eb'),
      backgroundColor: isSelected ? '#ecf5ff' : (hasPosition ? '#f0fdf4' : '#ffffff')
    })
    
    // Icono de firma
    const iconSignature = document.createElement('i')
    iconSignature.className = 'fas fa-signature'
    Object.assign(iconSignature.style, {
      color: hasPosition ? '#10b981' : '#ef4444',
      fontSize: '18px'
    })
    card.appendChild(iconSignature)
    
    // Nombre del firmante
    const nombreSpan = document.createElement('span')
    nombreSpan.className = 'flex-grow-1'
    nombreSpan.textContent = `${item.firmante.nombre || ''} ${item.firmante.apellidos || ''}`.trim()
    card.appendChild(nombreSpan)
    
    // Icono de estado (check o circle)
    const iconStatus = document.createElement('i')
    if (hasPosition) {
      iconStatus.className = 'fas fa-check-circle text-success'
    } else {
      iconStatus.className = 'fas fa-circle text-muted'
    }
    card.appendChild(iconStatus)
    
    // Event listener para el click
    card.addEventListener('click', () => {
      seleccionarFirmante(index)
      // Re-renderizar para actualizar estilos
      renderizarTarjetasFirmantes()
    })
    
    // Añadir al contenedor
    try {
      firmantesCardsContainer.value.appendChild(card)
    } catch (error) {
      console.warn('[renderizarTarjetasFirmantes] Error al añadir tarjeta:', error.message)
    }
  })
}

// Funciones de eventos del mouse
const onMouseMove = () => {
  // Preview deshabilitado - no se muestra el cuadrado azul con el número
}

const onClick = (e) => {
  if (!imgPdf.value || !overlayContainer.value) return
  
  e.preventDefault()
  e.stopPropagation()
  
  const rect = imgPdf.value.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top
  const cw = imgPdf.value.clientWidth
  const ch = imgPdf.value.clientHeight
  const iw = imgPdf.value.naturalWidth
  const ih = imgPdf.value.naturalHeight
  
  if (y >= ch - 25 || x >= cw - 70) return
  
  const idx = numFirmante.value
  if (!props.firmantes[idx]) return
  
  // Calcular coordenadas relativas al PDF original
  const px = (x / cw) * iw
  const py = (y / ch) * ih
  
  const currentPage = pages.value[paginaActual.value - 1]
  if (!currentPage) return
  
  // Guardar posición SOLO en el mapa
  const firmanteId = props.firmantes[idx].firmante.id
  const nuevaPosicion = {
    x: x,
    y: y,
    px: px,
    py: py,
    pagina: paginaActual.value
  }
  
  // Actualizar el mapa directamente
  posicionesMap.value.set(firmanteId, nuevaPosicion)
  
  // Renderizar marcadores y tarjetas de forma asíncrona
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (overlayContainer.value && imgPdf.value && overlayContainer.value.parentNode) {
        renderizarMarcadores()
      }
      // Re-renderizar tarjetas para actualizar el estado visual
      renderizarTarjetasFirmantes()
    })
  })
  
  emit('posicion-guardada', {
    firmanteId,
    posicion: nuevaPosicion
  })
  
  if (imgPdf.value) {
    imgPdf.value.style.border = '3px solid rgb(64, 158, 255)'
  }
}

const onMouseLeave = () => {
  // Preview deshabilitado - ya no se muestra el cuadrado azul
}

// Funciones de selección
const seleccionarFirmante = (index) => {
  numFirmante.value = index
  if (imgPdf.value && props.firmantes[index]) {
    const firmanteId = props.firmantes[index].firmante.id
    const tienePosicion = posicionesMap.value.get(firmanteId)
    imgPdf.value.style.border = tienePosicion ? '3px solid rgb(64, 158, 255)' : '3px solid darkgray'
  }
  // Re-renderizar tarjetas para actualizar estilos de selección
  renderizarTarjetasFirmantes()
}

const limpiarSeleccion = () => {
  posicionesMap.value.clear()
  numFirmante.value = 0
  
  if (imgPdf.value) {
    imgPdf.value.style.border = '3px solid darkgray'
  }
  
  // Limpiar marcadores del DOM
  if (overlayContainer.value) {
    while (overlayContainer.value.firstChild) {
      overlayContainer.value.removeChild(overlayContainer.value.firstChild)
    }
  }
  
  // Re-renderizar tarjetas para actualizar el estado visual
  renderizarTarjetasFirmantes()
  
  emit('posiciones-limpias')
}

// Exponer métodos y datos para el componente padre
defineExpose({
  obtenerPosiciones: () => {
    const posiciones = []
    props.firmantes.forEach(item => {
      const posicion = posicionesMap.value.get(item.firmante.id)
      if (posicion) {
        posiciones.push({
          ...item,
          posicion: posicion
        })
      }
    })
    return posiciones
  },
  todasPosicionesListas: computed(() => {
    return props.firmantes.length > 0 &&
      props.firmantes.every(p => posicionesMap.value.get(p.firmante.id))
  }),
  cargarPdf
})

// Watch para recargar cuando cambian los firmantes
watch(() => props.firmantes, () => {
  if (props.firmantes.length > 0 && !pdfCargado.value) {
    cargarPdf()
  }
  // Re-renderizar tarjetas cuando cambian los firmantes
  nextTick(() => {
    renderizarTarjetasFirmantes()
  })
}, { immediate: true })

// Lifecycle
onMounted(() => {
  if (props.firmantes.length > 0) {
    cargarPdf()
  }
})

onBeforeUnmount(() => {
  // Preview deshabilitado - no hay limpieza necesaria
})
</script>

<style scoped>
.firmante-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.pdf-container {
  display: inline-block;
  position: relative;
}
</style>


